import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Mail, KeyRound, Wallet, ScanLine, Crown, RotateCcw, Wrench, LifeBuoy, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHeader } from "@/components/PagePrimitives";
import Reveal from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";
import { SITE } from "@/config/site";

const ACCOUNT = [
  ["Creating an account", "Open Nova and choose to sign up. You can register with your email address or Sign in with Google."],
  ["Email login", "Enter the email address and password you registered with on the sign-in screen."],
  ["Sign in with Google", "Choose Sign in with Google and pick the Google account you want to use."],
  ["Email verification", "If prompted, open the verification email and follow the link to confirm your address. Check your spam folder if it does not arrive."],
  ["Forgotten password / reset", "On the sign-in screen, choose the forgotten-password option, enter your email and follow the reset link we send you."],
  ["Signing out", "Open Settings inside Nova and choose to sign out."],
  ["Updating account information", "You can update permitted account details in Settings inside the app."],
];

const FINANCE = [
  ["Adding income", "Add your income so Nova can build an accurate monthly picture."],
  ["Adding expenses", "Add one-off and everyday expenses to track your spending."],
  ["Adding recurring expenses", "Add subscriptions, bills and recurring payments so nothing catches you out."],
  ["Adding debts", "Add each debt with its balance so Nova can plan a payoff route."],
  ["Adding savings goals", "Create goals for emergencies, big purchases or clearing debt, and add to them over time."],
  ["Editing & deleting entries", "Open any entry to edit its details, or remove it if it is no longer needed."],
  ["Choosing a strategy", "Select Snowball, Avalanche or Custom to decide the order you tackle your debts."],
  ["Understanding forecasts & dates", "Estimated completion dates and forecasts are based on the information you enter and may change as you update it."],
];

const AIHELP = [
  ["Using the AI Coach", "Open the Coach tab to ask questions about your budget, debts and goals and get personalised guidance. Unlimited AI Coach access is a Nova Premium feature. Guidance is educational only and is not regulated financial advice."],
  ["Understanding Insights", "The Insights tab summarises your spending, highlights what changed and tracks your overall progress. Insights are estimates based on the information you enter."],
  ["Privacy & security", "Your information is associated with and protected within your own account, and Nova uses secure authentication. Nova does not use your data for advertising and does not sell it. See the Privacy Policy for full details."],
  ["Exporting your data", "You can export your data from within Nova. Export is a Nova Premium feature."],
  ["Deleting your account", "You can delete your account from inside Nova in Settings. See the Delete Account page for exactly what is removed and how to cancel an active subscription separately through Apple."],
];

const PREMIUM = [
  ["Weekly, Monthly & Annual plans", "Weekly is £1.99/week, Monthly is £3.99/month and Annual is £29.99/year. All three plans unlock the same Premium features. Only the billing duration differs. Local App Store prices may vary by region."],
  ["How to subscribe", "Open the Premium section in the app and choose a plan. Purchases are processed through Apple In-App Purchase and charged to your Apple Account; the price is displayed and confirmed before you purchase."],
  ["Automatic renewal", "Subscriptions renew automatically unless cancelled at least 24 hours before the end of the current billing period."],
  ["Managing or cancelling", "Subscriptions are purchased through Apple In-App Purchase. Manage or cancel them in your Apple subscription settings: Settings → your name → Subscriptions → Nova. Cancellation normally takes effect at the end of the current billing period."],
  ["Purchase succeeded but Premium didn't unlock", "Use Restore Purchases (see below). If it still doesn't unlock, contact support and we'll help verify your entitlement."],
  ["What happens after expiry", "When a subscription expires, Premium features are locked again while your data remains in your account."],
  ["Refunds", "App Store refund requests are handled by Apple. Nova cannot directly issue Apple refunds."],
];

const TROUBLE = [
  "App not loading. Close and reopen Nova, and make sure you're on the latest version.",
  "Login problems. Double-check your email and password, or use the forgotten-password option.",
  "Password-reset email not arriving. Check your spam folder and confirm you used the correct email.",
  "Subscription products not appearing. Check your internet connection and try reopening the Premium screen.",
  "Prices showing incorrectly. Prices are set and displayed by Apple for your App Store region; reopen the screen to refresh.",
  "Purchase pending. Some purchases need confirmation from Apple; give it a moment before retrying.",
  "Premium not unlocking. Use Restore Purchases with the correct Apple Account.",
  "Restore Purchases not working. Confirm you're signed in with the Apple Account used to buy, then contact support.",
  "Statement upload failure. Check the file matches what the upload screen accepts and try again.",
  "AI analysis unavailable. Try again shortly; AI features can be briefly unavailable during high demand.",
  "Notifications not appearing. Check notification permissions for Nova in your device settings.",
  "Data not synchronising. Confirm you're signed in and connected, then reopen the app.",
];

const RESTORE_STEPS = [
  "Open Nova on your device.",
  "Go to Settings (or the Premium page).",
  "Select Restore Purchases.",
  "Confirm you're signed in with the same Apple Account used for the original purchase.",
  "Wait a moment while your entitlement is verified.",
  "If Premium still isn't restored, contact support and we'll help.",
];

const Cat = ({ id, icon: Icon, title, items }) => (
  <Reveal>
    <div id={id} className="scroll-mt-28">
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-nova-cyan">
          <Icon size={18} />
        </div>
        <h2 className="font-heading text-2xl font-bold text-white">{title}</h2>
      </div>
      <Accordion type="single" collapsible className="rounded-2xl border border-white/10 bg-white/[0.02] px-5">
        {items.map(([q, a], i) => (
          <AccordionItem key={i} value={`${id}-${i}`} className="border-b border-white/8 last:border-0">
            <AccordionTrigger className="py-4 text-left text-sm font-semibold text-white hover:no-underline data-[state=open]:text-nova-cyan" data-testid={`support-${id}-trigger-${i}`}>
              {q}
            </AccordionTrigger>
            <AccordionContent className="pb-4 text-sm leading-relaxed text-white/60">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </Reveal>
);

export default function Support() {
  const toc = [
    ["account", "Account & login"],
    ["finance", "Financial features"],
    ["aihelp", "AI Coach, Insights & data"],
    ["statement", "Bank-statement analysis"],
    ["premium", "Nova Premium"],
    ["restore", "Restore Purchases"],
    ["troubleshooting", "Troubleshooting"],
    ["contact", "Contact support"],
  ];

  return (
    <>
      <SEO title="Nova Support Centre" description="Nova Support Centre. Help with your account, financial features, bank-statement analysis, AI Coach, Nova Premium, restoring purchases and troubleshooting." path="/support" />
      <PageHeader eyebrow="Help Centre" title="Nova Support Centre" subtitle="Find help with your account, features, Nova Premium, purchases and troubleshooting. Can't find an answer? Contact us at the bottom of this page." />

      <section className="pb-24">
        <div className="container-nova grid gap-12 lg:grid-cols-[240px_1fr]">
          {/* TOC */}
          <aside className="lg:sticky lg:top-28 lg:h-fit">
            <p className="label-eyebrow mb-4 text-white/50">On this page</p>
            <nav className="flex flex-col gap-1.5">
              {toc.map(([id, label]) => (
                <a key={id} href={`#${id}`} className="rounded-lg px-3 py-2 text-sm text-white/60 transition-colors hover:bg-white/5 hover:text-white" data-testid={`support-toc-${id}`}>
                  {label}
                </a>
              ))}
            </nav>
          </aside>

          <div className="space-y-16">
            <Cat id="account" icon={KeyRound} title="Account & login" items={ACCOUNT} />
            <Cat id="finance" icon={Wallet} title="Financial features" items={FINANCE} />
            <Cat id="aihelp" icon={Sparkles} title="AI Coach, Insights & data" items={AIHELP} />

            {/* Bank-statement analysis */}
            <Reveal>
              <div id="statement" className="scroll-mt-28">
                <div className="mb-5 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-nova-cyan"><ScanLine size={18} /></div>
                  <h2 className="font-heading text-2xl font-bold text-white">Bank-statement analysis</h2>
                </div>
                <div className="space-y-4 rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-sm leading-relaxed text-white/65">
                  <p><span className="font-semibold text-white">How to upload:</span> Open the Expenses area in Nova and choose “Scan bank statement for expenses”, then follow the prompts. Bank-statement analysis is a Nova Premium feature.</p>
                  <p><span className="font-semibold text-white">Supported file types & limits:</span> The exact file types and any size limits that Nova accepts are shown on the upload screen inside the app at the moment you upload. Please follow the guidance shown there.</p>
                  <p><span className="font-semibold text-white">What Nova analyses:</span> Nova reviews the statement you upload to help identify expenses and recurring payments so you can add them more easily.</p>
                  <p><span className="font-semibold text-white">What Nova does not do:</span> Nova does not connect directly to your bank and does not move money. It only works with documents you choose to upload.</p>
                  <p><span className="font-semibold text-white">Common upload errors:</span> If an upload fails, check that the file matches what the upload screen accepts, that it isn't password-protected, and that you have a stable connection, then try again.</p>
                  <p><span className="font-semibold text-white">Protecting your documents:</span> Keep downloaded statements secure and remove them from shared devices. Only upload documents that belong to you. Do not upload documents belonging to another person.</p>
                </div>
              </div>
            </Reveal>

            <Cat id="premium" icon={Crown} title="Nova Premium" items={PREMIUM} />

            {/* Restore purchases guide */}
            <Reveal>
              <div id="restore" className="scroll-mt-28">
                <div className="mb-5 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-nova-cyan"><RotateCcw size={18} /></div>
                  <h2 className="font-heading text-2xl font-bold text-white">Restore Purchases</h2>
                </div>
                <ol className="space-y-3 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                  {RESTORE_STEPS.map((s, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-white/70" data-testid={`restore-step-${i + 1}`}>
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-white/15 bg-nova-surface text-xs font-bold text-nova-cyan">{i + 1}</span>
                      {s}
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>

            {/* Troubleshooting */}
            <Reveal>
              <div id="troubleshooting" className="scroll-mt-28">
                <div className="mb-5 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-nova-cyan"><Wrench size={18} /></div>
                  <h2 className="font-heading text-2xl font-bold text-white">Troubleshooting</h2>
                </div>
                <ul className="grid gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:grid-cols-2">
                  {TROUBLE.map((t, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed text-white/65">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-nova-emerald" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* Contact */}
            <Reveal>
              <div id="contact" className="scroll-mt-28">
                <div className="mb-5 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-nova-cyan"><LifeBuoy size={18} /></div>
                  <h2 className="font-heading text-2xl font-bold text-white">Contact support</h2>
                </div>
                <p className="mb-4 text-sm text-white/60">
                  Still need help? Email{" "}
                  <a href={`mailto:${SITE.supportEmail}`} className="text-nova-cyan hover:underline" data-testid="support-email-link">{SITE.supportEmail}</a>{" "}
                  or use the form below. For account deletion, see the{" "}
                  <Link to="/delete-account" className="text-nova-cyan hover:underline">Delete Account</Link> page.
                </p>
                <ContactForm />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
